import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

// Finger travel before we commit to a gesture direction. Below this, a touch is
// still ambiguous (could become a horizontal tab-bar swipe or a vertical scroll),
// so we do NOT preventDefault — the browser keeps full control.
const ACTIVATION_PX = 8;
// Finger travel (past activation) past which a release fires the refresh.
const PULL_THRESHOLD_PX = 72;
const PULL_MAX_PX = 96;
// Finger travel is damped so the pull feels elastic rather than 1:1.
const PULL_RESISTANCE = 0.5;
// Quiet period after the feed stops fetching before the band collapses — covers
// the brief gap between the release and the re-keyed query actually starting.
const REFRESH_SETTLE_GRACE_MS = 250;
// Safety: never leave the spinner stuck if the fetch state never settles.
const REFRESH_MAX_MS = 6000;

const ARMED_HEIGHT_PX = PULL_THRESHOLD_PX * PULL_RESISTANCE;

// "pulling" → the user is dragging (show the arrow + hint); "refreshing" → the
// release armed a refresh and the feed is actually fetching (show the spinner).
export type PullStatus = "idle" | "pulling" | "refreshing";

type Gesture =
  | { phase: "idle" }
  | { phase: "deciding" | "pulling"; startX: number; startY: number };

// Recommender v2 — Phase 4. A touch pull-to-refresh bound to an existing scroll
// container (the one ScrollableLayout owns). Pulling down from the very top past
// the threshold and releasing fires `onRefresh` (the Discovery feed mints a new
// seed and refetches with a fresh rotation).
//
// Design points that matter:
//   • Own space, not an overlay. The content (`contentRef`) is pushed DOWN by a
//     `translateY`, opening a band at the top that the indicator (`indicatorRef`)
//     fills — so it never sits on top of the cards.
//   • Imperative offset. The pull offset is written straight to the DOM nodes on
//     each touchmove (transform/height = compositor, no reflow), and the band's
//     height is NOT a JSX `style` prop (a re-render would reset it) — it defaults
//     to `h-0` and is then owned by this hook.
//   • Status is React state, but only flips on transitions (commit / release /
//     fetch-settled), never per frame — so the page re-renders a couple of times
//     per gesture at most. The caller renders the arrow vs. spinner from it.
//   • The spinner shows ONLY while the feed is really fetching: `status` becomes
//     "refreshing" on an armed release and collapses once `isRefreshing` has gone
//     true then false again (with a safety timeout).
//   • Direction-committed. A gesture only becomes a pull once it has moved
//     ACTIVATION_PX and is predominantly DOWNWARD; a horizontal drag (the
//     DiscoveryTabBar filter strip) or an upward drag is left to the browser.
export function usePullToRefresh({
  scrollRef,
  enabled,
  isRefreshing,
  onRefresh,
}: {
  scrollRef: RefObject<HTMLDivElement | null>;
  enabled: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
}): {
  contentRef: RefObject<HTMLDivElement>;
  indicatorRef: RefObject<HTMLDivElement>;
  status: PullStatus;
} {
  const contentRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture>({ phase: "idle" });
  const distance = useRef(0);
  const [status, setStatus] = useState<PullStatus>("idle");
  const safetyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Push the content down by `px` and grow the spinner band to match, so the
  // indicator occupies its own freed space above the feed. Uses only refs → safe
  // to memoize with no deps.
  const setOffset = useCallback((px: number, animate: boolean) => {
    const band = indicatorRef.current;
    if (band !== null) {
      band.style.transition = animate ? "height 0.2s ease-out" : "none";
      band.style.height = `${px}px`;
    }
    const content = contentRef.current;
    if (content !== null) {
      content.style.transition = animate ? "transform 0.2s ease-out" : "none";
      content.style.transform = `translateY(${px}px)`;
    }
  }, []);

  const collapse = useCallback(() => {
    if (safetyTimer.current !== null) {
      clearTimeout(safetyTimer.current);
      safetyTimer.current = null;
    }
    setStatus("idle");
    setOffset(0, true);
    // Snap back to the very top: the content was translated during the pull, and a
    // refetch can leave the scroller a few px down via the browser's scroll
    // anchoring. Force scrollTop 0 so a refresh always lands at the top.
    const node = scrollRef.current;
    if (node !== null) node.scrollTop = 0;
  }, [scrollRef, setOffset, setStatus]);

  useEffect(() => {
    const node = scrollRef.current;
    if (node === null) return;
    if (!enabled) return;

    function handleTouchStart(e: TouchEvent) {
      const touch = e.touches[0];
      if (touch === undefined) return;
      // Only a drag that begins at the very top can become a pull.
      if (node === null || node.scrollTop > 0) {
        gesture.current = { phase: "idle" };
        return;
      }
      gesture.current = {
        phase: "deciding",
        startX: touch.clientX,
        startY: touch.clientY,
      };
      distance.current = 0;
    }

    function handleTouchMove(e: TouchEvent) {
      const g = gesture.current;
      if (g.phase === "idle") return;
      const touch = e.touches[0];
      if (touch === undefined) return;
      const dx = touch.clientX - g.startX;
      const dy = touch.clientY - g.startY;

      if (g.phase === "deciding") {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < ACTIVATION_PX) return;
        // Commit: only a predominantly-downward drag is a pull. Horizontal (tab
        // bar) or upward → abandon and let the browser scroll natively.
        if (dy <= 0 || Math.abs(dx) >= Math.abs(dy)) {
          gesture.current = { phase: "idle" };
          return;
        }
        gesture.current = { ...g, phase: "pulling" };
        setStatus("pulling");
      }

      // Engaged vertical pull: now (and only now) we own the gesture.
      e.preventDefault();
      const next = Math.min(
        (dy - ACTIVATION_PX) * PULL_RESISTANCE,
        PULL_MAX_PX
      );
      distance.current = next;
      setOffset(next, false);
    }

    function handleTouchEnd() {
      const wasPulling = gesture.current.phase === "pulling";
      const armed = distance.current >= ARMED_HEIGHT_PX;
      gesture.current = { phase: "idle" };
      distance.current = 0;
      if (!wasPulling) return;
      if (!armed) {
        setStatus("idle");
        setOffset(0, true);
        return;
      }
      // Hold the band, show the spinner, fire the refresh. The band collapses
      // once the feed has actually finished fetching (the isRefreshing effect).
      setStatus("refreshing");
      setOffset(ARMED_HEIGHT_PX, true);
      onRefresh();
      if (safetyTimer.current !== null) clearTimeout(safetyTimer.current);
      safetyTimer.current = setTimeout(collapse, REFRESH_MAX_MS);
    }

    node.addEventListener("touchstart", handleTouchStart, { passive: true });
    node.addEventListener("touchmove", handleTouchMove, { passive: false });
    node.addEventListener("touchend", handleTouchEnd, { passive: true });
    node.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      node.removeEventListener("touchstart", handleTouchStart);
      node.removeEventListener("touchmove", handleTouchMove);
      node.removeEventListener("touchend", handleTouchEnd);
      node.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [scrollRef, enabled, onRefresh, setOffset, setStatus, collapse]);

  // Collapse the band once a pull-triggered fetch has run its course. Re-runs on
  // every isRefreshing change: an active fetch keeps the band open; a short quiet
  // period (covering the gap before the re-keyed query starts) then collapses it.
  // Bounded by the safety timeout above, so it can never stick.
  useEffect(() => {
    if (status !== "refreshing") return;
    if (isRefreshing) return;
    const id = setTimeout(collapse, REFRESH_SETTLE_GRACE_MS);
    return () => {
      clearTimeout(id);
    };
  }, [isRefreshing, status, collapse]);

  useEffect(() => {
    return () => {
      if (safetyTimer.current !== null) clearTimeout(safetyTimer.current);
    };
  }, []);

  return { contentRef, indicatorRef, status };
}
